import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { toast } from "sonner";
import { FolderInput } from "lucide-react";
import { api } from "$convex/convexApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

type BulkMoveDialogProps = {
  authUserId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedDocumentIds: string[];
  onSuccess: () => void;
};

export function BulkMoveDialog({
  authUserId,
  open,
  onOpenChange,
  selectedDocumentIds,
  onSuccess,
}: BulkMoveDialogProps) {
  const [selectedFolderPath, setSelectedFolderPath] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const folderTree = useQuery(
    api.knowledgeBase.getFolderTree,
    open ? { authUserId } : "skip",
  );

  const bulkMoveDocuments = useMutation(api.knowledgeBase.bulkMoveDocuments);

  const allFolders = React.useMemo(() => {
    if (!folderTree?.folders) return [];

    const flattenFolders = (folders: any[], result: any[] = []): any[] => {
      for (const folder of folders) {
        result.push(folder);
        if (folder.children && folder.children.length > 0) {
          flattenFolders(folder.children, result);
        }
      }
      return result;
    };

    return flattenFolders(folderTree.folders);
  }, [folderTree]);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedFolderPath) {
      toast.error("Please select a folder");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await bulkMoveDocuments({
        authUserId,
        documentIds: selectedDocumentIds as any[],
        newFolderPath: selectedFolderPath,
      });

      toast.success(
        `Successfully moved ${result.movedCount} ${result.movedCount === 1 ? "document" : "documents"}`,
      );
      onSuccess();
      onOpenChange(false);
      setSelectedFolderPath("");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to move documents");
    } finally {
      setIsSubmitting(false);
    }
  }, [authUserId, bulkMoveDocuments, onOpenChange, onSuccess, selectedDocumentIds, selectedFolderPath]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <FolderInput className="size-5 text-primary" />
            <DialogTitle>Move documents to folder</DialogTitle>
          </div>
          <DialogDescription>
            Move {selectedDocumentIds.length}{" "}
            {selectedDocumentIds.length === 1 ? "document" : "documents"} to a different folder.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-select">Destination folder</Label>
            <Select value={selectedFolderPath} onValueChange={setSelectedFolderPath}>
              <SelectTrigger id="folder-select">
                <SelectValue placeholder="Select a folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Root (no folder)</SelectItem>
                {allFolders.map((folder) => (
                  <SelectItem key={folder.path} value={folder.path}>
                    {"  ".repeat(folder.depth)}
                    {folder.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !selectedFolderPath}>
            {isSubmitting ? "Moving..." : "Move documents"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
